import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree,
  CanActivateFn,
} from "@angular/router";
import { AuthService } from "src/app/services/auth.service";

/**
 *
 * Verifica l'autenticazione e reindirizza alla pagina di accesso se l'utente non è autenticato.
 */
@Injectable({
  providedIn: "root",
})
class AuthPermissionGuard {
  private auth = inject(AuthService);
  private router = inject(Router);

  /**
   *
   * Consente la navigazione agli utenti autenticati; altrimenti memorizza la destinazione e restituisce l'URL di accesso.
   * @param next Istantanea del percorso da attivare.
   * @param state Stato del router che contiene l'URL richiesto.
   * @returns `true` se la navigazione è consentita, altrimenti un albero URL per la pagina di accesso.
   */
  public canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): boolean | Promise<boolean | UrlTree> {
    if (this.auth.isAuthenticated()) {
      return true;
    }

    // Dopo un reload il token può esserci ancora, ma l'utente in memoria no.
    // Attendere il ripristino evita un redirect al login per una sessione valida.
    return this.auth.restoreSession().then((restored) => {
      if (restored) {
        return true;
      }

      this.auth.setRequestedPath(state.url);
      return this.router.parseUrl("/login");
    });
  }
}

/**
 *
 * Guardia funzionale dei percorsi che delega i controlli di accesso al servizio di autenticazione.
 * @param next Istantanea del percorso da attivare.
 * @param state Stato del router che contiene l'URL richiesto.
 * @returns `true` se la navigazione è consentita, altrimenti un albero URL per la pagina di accesso.
 */
export const AuthGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean | Promise<boolean | UrlTree> => {
  return inject(AuthPermissionGuard).canActivate(next, state);
};
